import type { Bindings, Logger } from 'pino';

import { logger } from './logger.js';

/**
 * Create a namespaced child logger derived from the application root logger.
 *
 * The supplied `name` is attached as a `name` binding on every record emitted by
 * the returned logger, which makes it trivial to filter logs by subsystem (e.g.
 * `name=auth` or `name=ws:gateway`). Additional static bindings can be supplied
 * and will be merged onto every record as well.
 *
 * Child loggers are cheap to create and inherit the parent's level, redaction
 * rules, serializers, and transport — so it is idiomatic to create one per
 * module, class, or long-lived component.
 *
 * @param name - Stable identifier for the subsystem, e.g. `"auth"`, `"api"`.
 * @param bindings - Optional extra fields merged onto every child record.
 * @returns A pino child {@link Logger}.
 *
 * @example
 * ```ts
 * const log = createLogger('projects');
 * log.debug({ projectId }, 'recalculating progress');
 * ```
 */
export function createLogger(name: string, bindings: Bindings = {}): Logger {
  return logger.child({ name, ...bindings });
}
